import { FC } from "react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import {
  cn,
  paginationNavBtn,
  paginationNavBtnDisabled,
  paginationPageActive,
  paginationPageInactive,
} from "@design-system";

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export const Pagination: FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className = "",
}) => {
  // Beregn hvilke sider som skal vises i pagineringen
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5; // Vis maks 5 sidetall om gangen

    if (totalPages <= maxPagesToShow) {
      // Hvis det er færre sider enn maxPagesToShow, vis alle
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Ellers, vis et begrenset antall sider med ellipsis
      if (currentPage <= 3) {
        // Hvis vi er nær starten
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("ellipsis");
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Hvis vi er nær slutten
        pageNumbers.push(1);
        pageNumbers.push("ellipsis");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // Hvis vi er i midten
        pageNumbers.push(1);
        pageNumbers.push("ellipsis");
        pageNumbers.push(currentPage - 1);
        pageNumbers.push(currentPage);
        pageNumbers.push(currentPage + 1);
        pageNumbers.push("ellipsis");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  // Håndter klikk på forrige side
  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  // Håndter klikk på neste side
  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  // Hvis det bare er én side, ikke vis paginering
  if (totalPages <= 1) return null;

  return (
    <nav
      className={`flex items-center justify-center mt-4 ${className}`}
      aria-label="Paginering"
    >
      <ul className="flex items-center -space-x-px">
        {/* Forrige-knapp */}
        <li>
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className={cn(
              "flex items-center justify-center px-3 h-8 ms-0 leading-tight rounded-l-lg border",
              currentPage === 1 ? paginationNavBtnDisabled : paginationNavBtn,
            )}
            aria-label="Forrige"
          >
            <HiChevronLeft className="w-5 h-5" />
          </button>
        </li>

        {/* Sidetall */}
        {getPageNumbers().map((page, index) => (
          <li key={index}>
            {page === "ellipsis" ? (
              <span className="flex items-center justify-center px-3 h-8 leading-tight text-muted bg-surface border border-border-strong dark:bg-surface-muted dark:border-border">
                ...
              </span>
            ) : (
              <button
                onClick={() => typeof page === "number" && onPageChange(page)}
                className={
                  currentPage === page
                    ? paginationPageActive
                    : paginationPageInactive
                }
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </button>
            )}
          </li>
        ))}

        {/* Neste-knapp */}
        <li>
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className={cn(
              "flex items-center justify-center px-3 h-8 leading-tight rounded-r-lg border",
              currentPage === totalPages
                ? paginationNavBtnDisabled
                : paginationNavBtn,
            )}
            aria-label="Neste"
          >
            <HiChevronRight className="w-5 h-5" />
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default Pagination;
